import { useState } from 'react'
import useSWR from 'swr'
import styled from 'styled-components'
import type { PnlResult } from './pnl'
import { Card, H2, Muted, Row, Tag } from './ui'

const usd = (v: number) => {
  const s = Math.abs(v) >= 1000 ? Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 0 }) : Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 2 })
  return `${v < 0 ? '−' : ''}$${s}`
}
const signed = (v: number) => (v > 0 ? `+${usd(v)}` : usd(v))
const Big = styled.div<{ $v: number }>`
  font-size: 28px;
  font-weight: 800;
  line-height: 1.2;
  color: ${({ $v, theme }) => ($v > 0 ? '#31d0aa' : $v < 0 ? '#ff6b8a' : theme.colors.text)};
`
const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin: 12px 0;
  div {
    min-width: 0;
  }
  small {
    display: block;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
`
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  th {
    text-align: left;
    font-weight: 600;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.textSubtle};
    padding: 6px 4px;
  }
  td {
    padding: 8px 4px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    color: ${({ theme }) => theme.colors.text};
  }
  td:not(:first-child),
  th:not(:first-child) {
    text-align: right;
  }
`
const Details = styled.details`
  margin-top: 12px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSubtle};
  summary {
    cursor: pointer;
    min-height: 44px;
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.text};
  }
  li {
    margin: 4px 0;
    line-height: 1.45;
  }
`

/** Whether the server shows profit at all (flag SOCIAL_PNL_ENABLED). Unknown or failed = off. */
export function usePnlEnabled(): boolean {
  const { data } = useSWR<{ enabled: boolean }>('/api/social/pnl?status=1', (u: string) => fetch(u).then((r) => r.json()), { revalidateOnFocus: false })
  return Boolean(data?.enabled)
}

export function PnlCard({ wallet }: { wallet: string }) {
  const [tab, setTab] = useState<'open' | 'closed'>('open')
  const { data, error } = useSWR<PnlResult & { bnbUsd: number }>(`/api/social/pnl?wallet=${wallet}`, async (u: string) => {
    const r = await fetch(u)
    if (!r.ok) throw new Error(String(r.status))
    return r.json()
  }, { revalidateOnFocus: false })
  const rows = data?.tokens.filter((t) => (tab === 'open' ? t.size > 0 : t.size === 0)) ?? []
  return (
    <Card data-testid="pnl-card">
      <Row style={{ justifyContent: 'space-between' }}>
        <H2 style={{ margin: 0 }}>Profit</H2>
        <Tag $tone="warn">Estimate</Tag>
      </Row>
      {error && <Muted style={{ marginTop: 8 }}>Profit is not available right now.</Muted>}
      {!data && !error && <Muted style={{ marginTop: 8 }}>Reading trades…</Muted>}
      {data && (
        <>
          <Big $v={data.totals.realizedUsd + data.totals.unrealizedUsd} data-testid="pnl-total">
            {signed(data.totals.realizedUsd + data.totals.unrealizedUsd)}
          </Big>
          <Stats>
            <div>
              <small>Realized</small>
              <strong>{signed(data.totals.realizedUsd)}</strong>
            </div>
            <div>
              <small>Unrealized</small>
              <strong>{signed(data.totals.unrealizedUsd)}</strong>
            </div>
            <div>
              <small>Bought</small>
              <strong>{usd(data.totals.boughtUsd)}</strong>
            </div>
          </Stats>
          {data.counted === 0 ? (
            <Muted>No trades in CADINU pools yet.</Muted>
          ) : (
            <>
              <Row role="tablist" style={{ gap: 8, marginBottom: 4 }}>
                {(['open', 'closed'] as const).map((t) => (
                  <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)} style={{ minHeight: 44, padding: '0 14px', borderRadius: 12, border: 0, cursor: 'pointer', fontWeight: 700, fontSize: 15, background: tab === t ? 'rgba(127,212,232,0.18)' : 'transparent', color: 'inherit' }}>
                    {t === 'open' ? 'Open' : 'Closed'}
                  </button>
                ))}
              </Row>
              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <thead>
                    <tr>
                      <th>Token</th>
                      <th>{tab === 'open' ? 'Value' : 'Sold'}</th>
                      <th>PnL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((t) => (
                      <tr key={t.token}>
                        <td>{t.symbol}</td>
                        <td>{usd(tab === 'open' ? t.valueUsd : t.soldUsd)}</td>
                        <td style={{ color: t.realizedUsd + t.unrealizedUsd >= 0 ? '#31d0aa' : '#ff6b8a' }}>{signed(t.realizedUsd + t.unrealizedUsd)}</td>
                      </tr>
                    ))}
                    {!rows.length && (
                      <tr>
                        <td colSpan={3}>Nothing {tab}.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </>
          )}
          <Details>
            <summary>How this is computed</summary>
            <ul>
              <li>Trades through CADINUdex V2 pools sent by this wallet, read from each transaction&apos;s own token transfers to and from the wallet ({data.counted} counted{data.truncated ? ', latest 200 transactions only' : ''}).</li>
              <li>Average cost against BNB, USDT, BUSD or USDC. {data.skipped} token-to-token trades skipped; {data.notOwn} transactions where the wallet&apos;s own balances did not move (bots, contracts) excluded.</li>
              <li>BNB valued at today&apos;s price (${data.bnbUsd.toFixed(0)}), so older trades are approximate. V3 pools, other DEXes, transfers and airdrops are not included.</li>
              {data.unmatchedSellUsd > 0 && <li>{usd(data.unmatchedSellUsd)} of sales were of tokens not bought here; they are not counted as profit.</li>}
            </ul>
          </Details>
        </>
      )}
    </Card>
  )
}
